import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahbe8bp14a1h3mr6hog';

const topicOptions = ['线索投稿', '内容纠错', '商务合作', '其他'];

type FormResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value !== '') {
        payload.append(key, value);
      }
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const code = parsed?.code;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          serverMsg ? String(serverMsg) : '提交没有成功，请稍后重试，或直接发送邮件给我们。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交没有成功。你可以稍后重试，或直接发送邮件给我们。');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center rounded-lg border border-secondary-200 bg-secondary-50 px-6 py-12 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
          <i className="ri-checkbox-circle-line text-3xl"></i>
        </span>
        <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">已经收到，谢谢！</h2>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
          我们会先核对材料的来源与时间点，确认后再决定是否整理进复盘。一般两个工作日内会有反馈。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-6 cursor-pointer whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-100"
        >
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      id="guatian-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-100/50 p-5 md:p-7"
      noValidate={false}
    >
      <h2 className="font-heading text-xl font-bold text-foreground-950">线索投稿 / 合作咨询</h2>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        带原始链接和时间点的材料核实效率最高。所有信息仅用于内容整理与回复，不会对外公开你的联系方式。
      </p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-2">
          <span className="text-sm text-foreground-800">称呼</span>
          <input
            type="text"
            name="name"
            required
            placeholder="怎么称呼你"
            className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm text-foreground-800">邮箱</span>
          <input
            type="email"
            name="email"
            required
            placeholder="用于接收回复"
            className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm text-foreground-800">联系类型</span>
          <select
            name="topic"
            defaultValue={topicOptions[0]}
            className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
          >
            {topicOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm text-foreground-800">相关链接（选填）</span>
          <input
            type="text"
            name="link"
            placeholder="原始动态 / 报道链接"
            className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-2">
        <span className="text-sm text-foreground-800">详细说明</span>
        <textarea
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="简单描述事件经过、你掌握的时间点，以及希望我们梳理的方向（500 字以内）"
          className="resize-y rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        ></textarea>
      </label>

      <input
        type="text"
        name="website_alt"
        className="remark-extra-note"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === 'error' && formError ? (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 px-4 py-3 text-sm text-accent-800">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>{formError}</span>
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-foreground-500">提交即表示你同意我们对材料进行核实与整理。</p>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
          {status === 'submitting' ? '提交中…' : '提交信息'}
        </button>
      </div>
    </form>
  );
}